import React, { useState, useEffect } from 'react';
import { Mail, Loader2, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Safe localStorage wrapper for iframe environments
const safeStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn('localStorage not available, using memory fallback');
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn('localStorage not available');
    }
  }
};

const EmailCapturePopup = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user is already logged in - don't show popup
    const token = localStorage.getItem('token');
    if (token) {
      return; // User is logged in, no need to capture email
    }
    
    // Check if user has already submitted or dismissed (stored for 30 days)
    const hasSubmitted = safeStorage.getItem('email_popup_submitted');
    const hasDismissed = safeStorage.getItem('email_popup_dismissed');
    const submittedAt = safeStorage.getItem('email_popup_submitted_at');
    const dismissedAt = safeStorage.getItem('email_popup_dismissed_at');
    
    // Check submission expiry (30 days)
    if (hasSubmitted && submittedAt) {
      const daysPassed = (Date.now() - parseInt(submittedAt)) / (1000 * 60 * 60 * 24);
      if (daysPassed < 30) {
        return; // Don't show for 30 days after submission
      }
    }
    
    // Check dismissal expiry (30 days)
    if (hasDismissed && dismissedAt) {
      const daysPassed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysPassed < 30) {
        return; // Don't show for 30 days after dismissal
      }
    }
    
    // Show popup after 2 seconds
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API}/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsSuccess(true);
        // Store with timestamp for 30-day expiry
        safeStorage.setItem('email_popup_submitted', 'true');
        safeStorage.setItem('email_popup_submitted_at', Date.now().toString());
        safeStorage.setItem('email_popup_email', email);
        
        // Close popup after 3 seconds
        setTimeout(() => {
          setIsOpen(false);
        }, 3000);
      } else {
        setError(data.detail || data.message || 'Παρουσιάστηκε σφάλμα');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError('Παρουσιάστηκε σφάλμα. Παρακαλώ δοκιμάστε ξανά.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    // Store dismissal with timestamp
    safeStorage.setItem('email_popup_dismissed', 'true');
    safeStorage.setItem('email_popup_dismissed_at', Date.now().toString());
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto"
      style={{ backgroundColor: 'rgba(26, 7, 48, 0.95)' }}
      data-testid="email-capture-popup"
    >
      <div 
        className="relative w-full max-w-lg rounded-2xl shadow-2xl border border-purple-800/50 overflow-hidden my-auto max-h-[95vh] overflow-y-auto"
        style={{ 
          background: 'linear-gradient(180deg, #1A0730 0%, #2D0C4B 50%, #1A0730 100%)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 60px rgba(138, 79, 219, 0.2)'
        }}
      >
        {/* Close button removed per user request - user must submit email or interact with form */}
        
        {/* Content */}
        <div className="p-6 sm:p-8 text-center">
          {/* Logo - Μικρότερο στο mobile */}
          <div className="mb-4 sm:mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_46efea26-c11f-4465-8301-e5825bce2a76/artifacts/ma9aqpxu_logo_transparent.png"
              alt="Radio Solutions"
              className="h-20 sm:h-28 mx-auto"
            />
          </div>
          
          {isSuccess ? (
            // Success state
            <div className="py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">
                {t('popupSuccessTitle')}
              </h2>
              <p className="text-gray-300">
                {t('popupSuccessMessage')}
              </p>
            </div>
          ) : (
            // Form state
            <>
              {/* H1 - Radio Apps (μεγάλο, κάτω από το logo) */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 sm:mb-3 leading-tight">
                Radio Apps
              </h1>
              
              {/* H2 - Subtitle (μικρότερο) */}
              <h2 className="text-lg sm:text-xl md:text-2xl text-purple-300 font-semibold mb-4 sm:mb-5 leading-tight">
                {t('popupTitle')}
              </h2>
              
              {/* Mini Tutorial - Platform Features */}
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-purple-900/30 rounded-xl border border-purple-700/30">
                <p className="text-purple-200 text-sm font-medium mb-3">{t('popupPlatformOffers')}</p>
                <div className="space-y-2 text-left">
                  <div className="flex items-start gap-2">
                    <span className="text-orange-400 mt-0.5">📻</span>
                    <p className="text-gray-300 text-sm">
                      <span className="text-white font-medium">On Air Content</span> - {t('popupOnAirContent')}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-orange-400 mt-0.5">🔍</span>
                    <p className="text-gray-300 text-sm">
                      <span className="text-white font-medium">Song Finder</span> - {t('popupSongFinder')}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-orange-400 mt-0.5">📊</span>
                    <p className="text-gray-300 text-sm">
                      <span className="text-white font-medium">Top Radio Charts</span> - {t('popupTopCharts')}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-orange-400 mt-0.5">📈</span>
                    <p className="text-gray-300 text-sm">
                      <span className="text-white font-medium">Songs Analyzer</span> <span className="text-[#00FF88] font-bold text-xs">PRO</span> - {t('popupSongsAnalyzer')}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-orange-400 mt-0.5">🧮</span>
                    <p className="text-gray-300 text-sm">
                      <span className="text-white font-medium">Calculators</span> - {t('popupCalculators')}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-400 w-5 h-5" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('popupEmailPlaceholder')}
                    required
                    disabled={isLoading}
                    className="w-full pl-12 pr-4 py-4 bg-purple-900/40 border border-purple-600/50 rounded-xl text-white placeholder-purple-300/60 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    data-testid="email-input"
                  />
                </div>
                
                {error && (
                  <p className="text-red-400 text-sm">{error}</p>
                )}
                
                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full py-4 px-6 bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-500 hover:to-purple-700 text-white font-bold rounded-xl transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 shadow-lg shadow-purple-900/50"
                  data-testid="submit-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('popupSubmitting')}
                    </>
                  ) : (
                    t('popupSubmitButton')
                  )}
                </button>
              </form>
              
              {/* Bottom tagline */}
              <p className="mt-5 text-gray-400 text-xs leading-relaxed">
                📩 {t('popupBottomTagline')}
              </p>
              
              {/* Skip link - dismisses for 30 days */}
              <button
                onClick={handleDismiss}
                className="mt-3 text-purple-400/70 hover:text-purple-300 text-xs transition-colors underline underline-offset-2"
                data-testid="skip-email-btn"
              >
                {t('continueWithoutSignup')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailCapturePopup;
